import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import Loader from '../components/Loader';
import { ErrorFlash } from '../components/Flash';
import RegisterPage from '../components/RegisterPage';
import { ProfileContext, ProfileManager } from '../profile';
import { useRequestAsync, APIRequest, getHeaders } from '../api/client';
import UserInfoForm from './register/UserInfoForm';
import CompanyInfoForm from './register/CompanyInfoForm';

export interface UserRegistration {
  name: string;
  country: string;
  email: string;
  password: string;
  company_name?: string;
}

const registerUser = (context: ProfileManager): [APIRequest, Function] => {
  const [state, submit] = useRequestAsync((data: UserRegistration) => ({
    url: '/users/register',
    method: 'POST',
    body: JSON.stringify(data),
    headers: getHeaders(context),
  }));

  React.useEffect(() => {
    if (!state.data) {
      return;
    }

    context.set(state.data);
  }, [state]);

  return [state, submit];
};

const registerCompany = (context: ProfileManager): [APIRequest, Function] => {
  return useRequestAsync((data: UserRegistration) => ({
    url: '/companies/new',
    method: 'POST',
    body: JSON.stringify(data),
    headers: getHeaders(context),
  }));
};

const getButtonLabel = (step: number) => {
  return step === 0 ? 'Continue →' : 'Complete';
};

const headingTitles = ['Get Started', 'Create a profile'];

const subtitles = [
  'Create an account for your coffee brand for FREE.',
  'You can also use these details to login in the Filtru app.',
];

const SectionTitle = ({ page }: { page: number }) => (
  <h3 className="f3 f2-ns lh-title ma0 mb2 black">{headingTitles[page]}</h3>
);

const SectionSubtitle = ({ page }: { page: number }) => (
  <small className="f6 black-60 db mb4 lh-copy">{subtitles[page]}</small>
);

interface Props extends RouteComponentProps<any> {}

export default function Register(props: Props) {
  const context: any = React.useContext(ProfileContext);

  const [companyName, setCompanyName] = React.useState(null);
  const [companyLocation, setCompanyLocation] = React.useState(null);
  const [userName, setUserName] = React.useState(null);
  const [userEmail, setUserEmail] = React.useState(null);
  const [userPassword, setUserPassword] = React.useState(null);
  const [step, setStep] = React.useState(0);

  const [state, submit] = registerUser(context);
  const [companyState, companySubmit] = registerCompany(context);

  React.useEffect(() => {
    if (state.complete && context.profile && !companyState.complete) {
      companySubmit({
        name: companyName,
        contact_email: userEmail,
        description_text: `${companyName} from ${companyLocation}`,
        location: {
          country: companyLocation,
        },
      });
    }

    // Refresh the profile context with newly registered company
    if (state.complete && companyState.complete) {
      context.set(
        Object.assign({}, context.profile, {
          company_id: companyState.data.id,
          company: companyState.data,
        }),
      );
    }
  }, [state, companyState, context]);

  const getForm = (step: number) => {
    if (step === 0) {
      return (
        <CompanyInfoForm
          companyName={companyName}
          companyLocation={companyLocation}
          setCompanyName={setCompanyName}
          setCompanyLocation={setCompanyLocation}
        />
      );
    }

    return (
      <UserInfoForm
        userName={userName}
        userEmail={userEmail}
        setUserName={setUserName}
        setUserEmail={setUserEmail}
        setUserPassword={setUserPassword}
      />
    );
  };

  if (companyState.complete && companyState.data) {
    props.history.push('/dashboard');
    return <Loader />;
  }

  return (
    <RegisterPage {...props}>
      <form
        className="pa4 black-80 ml-auto mr-auto w-70-ns"
        method="POST"
        onSubmit={(e) => {
          e.preventDefault();

          if (step === 0) {
            setStep(step + 1);
            return;
          }

          submit({
            name: userName,
            email: userEmail,
            password: userPassword,
            country: companyLocation,
          });
        }}
      >
        <SectionTitle page={step} />
        <SectionSubtitle page={step} />
        {state.error && <ErrorFlash error={state.error} />}
        {companyState.error && <ErrorFlash error={companyState.error} />}
        {step > 0 && (
          <button
            type="button"
            className="mv3 db link black-70 f5 b bw0 bg-ui-white-1 tl pa0 dim pointer bg-animate"
            onClick={() => setStep(0)}
          >
            {'< Back'}
          </button>
        )}
        {state.loading ? <Loader /> : getForm(step)}
        <button
          className="mv3 mv4-ns db link gold f3 b bw0 bg-ui-white-1 tl pa0 dim pointer bg-animate"
          type="submit"
          disabled={companyState.complete && companyState.data}
        >
          {getButtonLabel(step)}
        </button>
      </form>
    </RegisterPage>
  );
}
